<template>
  <div v-if="experience" class="relative border border-[#373737] rounded-2xl p-4" data-e2e="experience-card">

    <!-- BIG Approved banner: only for "proven" experiences (really happened on the platform) -->
    <div
      v-if="isProven"
      class="big-approved-banner"
      role="img"
      :aria-label="t('experience_card.big_approved_aria')"
      data-e2e="experience-card-big-approved"
    >
      <svg class="big-approved-banner__mark" viewBox="24 24 630 432" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M24 455.761H166.88L205.206 421.819V295.271L24 455.761Z" fill="#D8ADFF" />
        <path d="M478.189 109.573C490.369 114.636 500.85 121.727 509.645 130.845C513.508 134.556 517.006 138.579 520.139 142.893L654.383 24H511.503L424.033 101.468H439.625C453.497 101.468 466.348 104.172 478.189 109.573Z" fill="#D8ADFF" />
        <path d="M439.625 101.469C453.498 101.469 466.348 104.172 478.19 109.573C490.369 114.636 500.85 121.727 509.645 130.846C513.508 134.557 516.999 138.579 520.139 142.893C524.42 148.778 528.031 155.226 530.958 162.244C536.368 174.066 539.076 186.896 539.076 200.739C539.076 216.949 535.691 231.806 528.927 245.312V245.305C522.163 258.473 512.864 269.612 501.022 278.731C512.864 287.849 522.163 299.162 528.927 312.667C535.691 326.173 539.076 340.858 539.076 356.73C539.076 370.573 536.368 383.575 530.958 395.729C525.887 407.545 518.778 418.015 509.645 427.134C500.85 435.908 490.369 442.998 478.19 448.406C466.348 453.469 453.498 456 439.625 456H205.206V421.951L311.759 327.631V369.983H404.108C409.856 369.983 414.761 367.955 418.823 363.906C423.217 359.513 425.421 354.45 425.421 348.711C425.421 342.972 423.217 338.075 418.823 334.026C414.761 329.632 409.856 327.439 404.108 327.439H311.978L415.205 236.061C416.042 235.318 416.838 234.536 417.588 233.715C422.931 227.883 425.932 220.242 425.932 212.263V207.751C425.932 196.77 417.018 187.87 406.019 187.87H326.475V187.877H326.222L205.266 294.828C205.339 219.315 205.425 223.854 205.206 101.469H439.625Z" fill="white" />
      </svg>
      <span class="big-approved-banner__label">{{ t('experience_card.big_approved') }}</span>
      <svg class="big-approved-banner__check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </div>

    <!-- Header: Company + Badges + Solliciter button -->
    <div class="flex justify-between items-center mb-2">
      <div class="flex items-center gap-3">
        <div class="h-12 w-12 bg-gray-600 rounded-xl flex items-center justify-center overflow-hidden">
          <img v-if="experience.company?.logo_url" :src="experience.company.logo_url" :alt="experience.company.public_name" class="h-full w-full object-cover" />
          <span v-else class="text-white text-lg font-semibold">{{ experience.company?.public_name?.charAt(0) || '?' }}</span>
        </div>
        <h2 class="text-white text-xl font-semibold">{{ props.experience?.company?.public_name }}</h2>
      </div>
      <!-- When proven, drop the owner controls below the BIG Approved ribbon (self-start + mt)
           so they never sit under the banner. In public view this cluster is empty. -->
      <div class="relative z-[2] flex items-center gap-3" :class="{ 'self-start mt-8': isProven }">
        <!-- Mission en cours badge -->
        <span
          v-if="isOngoing"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#363D69] text-white border border-[#4A5282]"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-[#6B7AE8]"></span>
          {{ t('experience_card.ongoing_mission') }}
        </span>
        <!-- Completion badge (shown when < 100%, only to profile owner) -->
        <span
          v-if="completionPercentage < 100 && isOwner"
          class="relative group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#2a2a2a] text-gray-300 border border-[#444] cursor-default select-none"
        >
          <span class="text-gray-400">{{ t('experience_card.completed_at') }}</span>
          {{ completionPercentage }}%
          <!-- Custom tooltip -->
          <span
            class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 delay-75 whitespace-nowrap z-50 pointer-events-none border border-gray-700"
          >
            {{ t('experience_card.completion_tooltip') }}
            <!-- Arrow -->
            <span class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
          </span>
        </span>
        <!-- Experience weight badge (50% or 75%, only to profile owner) -->
        <span
          v-if="showWeightBadge && isOwner"
          class="relative group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-900/30 text-amber-400 border border-amber-700/50 cursor-default select-none"
        >
          <Icon name="solar:danger-triangle-linear" size="0.9rem" />
          {{ weightBadgeText }}
          <!-- Custom tooltip (appears below to avoid clipping at top) -->
          <span
            class="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 delay-75 whitespace-normal max-w-xs z-[100] pointer-events-none border border-gray-700"
          >
            {{ weightTooltipText }}
            <!-- Arrow pointing up -->
            <span class="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></span>
          </span>
        </span>
        <!-- Solliciter une recommandation button - hidden for ongoing missions and incomplete experiences -->
        <b-button v-if="isOwner && !isOngoing && completionPercentage === 100" size="sm" light @click="onCreateFeedback">
          {{ $t('experience_card.ask_for_recommendation') }}
        </b-button>
      </div>
    </div>

    <div class="flex">
      <!-- Vertical line with dot -->
      <div class="bg-[#D3A1FF] w-[1px] relative mx-4">
        <div class="absolute top-1 bg-[#D3A1FF] rounded-full -translate-x-[50%] left-[50%] h-2 w-2"></div>
      </div>

      <div class="ml-4 flex-1">
        <!-- Title row with action buttons on the right -->
        <div class="flex justify-between items-start mb-1">
          <h3 class="text-white text-lg" data-e2e="experience-card-title">{{ props.experience?.title || props.experience?.name }}</h3>
          <!-- Action buttons (edit/delete/split) -->
          <div v-if="isOwner" class="flex items-center gap-1 flex-shrink-0 ml-4">
            <b-button
              v-if="canSplit"
              size="sm"
              light
              prepend-icon="solar:scissors-line-duotone"
              data-e2e="experience-card-split-button"
              @click="splitModalOpen = true"
            >
              {{ $t('experience_split.split_action') }}
            </b-button>
            <b-button size="sm" light @click="onUpdateClick" data-e2e="experience-card-edit-button">
              <Icon name="solar:pen-new-square-line-duotone" size="1.2rem" />
            </b-button>
            <b-button size="sm" light @click="onDeleteClick" data-e2e="experience-card-delete-button">
              <Icon name="solar:trash-bin-trash-bold-duotone" size="1.2rem" />
            </b-button>
          </div>
        </div>

        <!-- Info line: Location • Contract type • Dates • Duration -->
        <div class="text-gray-400 mb-4">
          <span v-if="experience.location">{{ experience.location }} • </span>
          <span v-if="experience.contract_type">{{ contractTypeLabel }} • </span>
          {{ formattedStartDate }} - {{ formattedEndDate }} • {{ getDuration }}
        </div>

        <!-- Bottom section: Pipeline card + Périmètre card + Reco cards -->
        <div class="flex gap-4">
          <!-- Pipeline / Revenue card - always shown -->
          <div class="border border-[#373737] rounded-xl px-4 py-3 flex-shrink-0 min-w-[200px]">
            <div class="text-gray-400 text-sm mb-2">
              {{ t('experience_card.pipeline_or_revenue') }}
            </div>
            <div>
              <template v-if="experience.pipeline_revenue && parseFloat(experience.pipeline_revenue) > 0">
                <span class="text-white text-2xl font-semibold">{{ formattedPipelineRevenue }}€</span>
              </template>
              <template v-else-if="isOngoing">
                <span class="text-white text-2xl font-semibold">{{ t('experience_card.in_progress') }}</span>
              </template>
              <template v-else>
                <span class="text-gray-300 text-sm">-</span>
              </template>
            </div>
          </div>

          <!-- Périmètre de la mission card -->
          <div class="flex-1 border border-[#373737] rounded-xl px-4 py-3">
            <div class="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <Icon name="solar:suitcase-line-duotone" size="1.2rem" />
              {{ t('experience_card.mission_scope') }}
            </div>
            <div class="text-gray-300 text-sm">
              <template v-if="experience.description">
                <p class="whitespace-pre-line line-clamp-3">{{ experience.description }}</p>
                <button
                  type="button"
                  class="mt-1 text-gray-400 underline hover:text-gray-300"
                  data-e2e="experience-scope-see-more"
                  @click="showScopeModal = true"
                >
                  {{ t('experience_card.see_more') }}
                </button>
              </template>
              <template v-else>
                {{ t('experience_card.no_scope_available') }}
              </template>
            </div>
          </div>

          <!-- Reco cards container (horizontal) - only shown when mission is completed and experience is 100% complete -->
          <div v-if="!hideFeedbacks && !isOngoing && completionPercentage === 100" class="flex gap-3 flex-shrink-0">
            <!-- Reco Client card -->
            <div class="border border-[#373737] rounded-xl p-4 flex flex-col gap-3 min-w-[140px]">
              <div class="flex items-start justify-between gap-4">
                <button
                  v-if="isOwner"
                  class="text-gray-400 text-sm underline underline-offset-2 hover:text-gray-300 transition-colors text-left leading-tight"
                  @click="onCreateFeedback"
                >
                  {{ t('experience_card.client_reco_line1') }}<br />{{ t('experience_card.client_reco_line2') }}
                </button>
                <span v-else class="text-gray-400 text-sm text-left leading-tight">
                  {{ t('experience_card.client_reco_line1') }}<br />{{ t('experience_card.client_reco_line2') }}
                </span>
                <b-button v-if="isOwner" size="sm" light @click="onCreateFeedback" class="flex-shrink-0">
                  <Icon name="solar:add-circle-linear" size="1.2rem" />
                </b-button>
              </div>
              <div class="flex justify-center text-gray-500">
                <Icon v-if="!customerFeedback || customerFeedback.status === 'open'" name="solar:question-circle-linear" size="2rem" />
                <Icon v-else name="solar:check-circle-linear" size="2rem" class="text-green-400" />
              </div>
            </div>

            <!-- Reco Manager card (only for permanent/fixed_term/internship/work_study) -->
            <div v-if="isManagerType" class="border border-[#373737] rounded-xl p-4 flex flex-col gap-3 min-w-[140px]">
              <div class="flex items-start justify-between gap-4">
                <button
                  v-if="isOwner"
                  class="text-gray-400 text-sm underline underline-offset-2 hover:text-gray-300 transition-colors text-left leading-tight"
                  @click="onCreateFeedback"
                >
                  {{ t('experience_card.manager_reco_line1') }}<br />{{ t('experience_card.manager_reco_line2') }}
                </button>
                <span v-else class="text-gray-400 text-sm text-left leading-tight">
                  {{ t('experience_card.manager_reco_line1') }}<br />{{ t('experience_card.manager_reco_line2') }}
                </span>
                <b-button v-if="isOwner" size="sm" light @click="onCreateFeedback" class="flex-shrink-0">
                  <Icon name="solar:add-circle-linear" size="1.2rem" />
                </b-button>
              </div>
              <div class="flex justify-center text-gray-500">
                <Icon v-if="!managerFeedback || managerFeedback.status === 'open'" name="solar:question-circle-linear" size="2rem" />
                <Icon v-else name="solar:check-circle-linear" size="2rem" class="text-green-400" />
              </div>
            </div>
          </div>
        </div>

        <!-- Public positive feedbacks (verbatims) — visible to everyone (ticket #174) -->
        <div v-if="publicPositiveFeedbacks.length" class="mt-4 space-y-3" data-e2e="experience-public-feedbacks">
          <figure
            v-for="f in displayedFeedbacks"
            :key="f.mission_feedback_id"
            class="relative rounded-xl border-l-2 pl-4 pr-3 py-3 bg-gradient-to-r to-transparent"
            :class="[f.accent.border, f.accent.gradient]"
          >
            <blockquote
              class="text-gray-200 text-sm italic leading-relaxed"
              :class="{ 'line-clamp-3': !expandedFeedbacks[f.mission_feedback_id] }"
            >« {{ f.comment }} »</blockquote>
            <button
              v-if="isLongFeedback(f)"
              type="button"
              class="mt-1 text-xs text-gray-400 hover:text-gray-300"
              @click="toggleFeedback(f.mission_feedback_id)"
            >
              {{ expandedFeedbacks[f.mission_feedback_id] ? t('experience_card.feedback.see_less') : t('experience_card.see_more') }}
            </button>
            <figcaption class="mt-2 flex items-center gap-2 text-xs">
              <span
                class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium"
                :class="[f.accent.chipBg, f.accent.chipText]"
              >
                <Icon name="solar:check-circle-linear" size="0.8rem" />
                {{ f.accent.label }}
              </span>
              <span class="text-gray-500">{{ companyName }}</span>
            </figcaption>
          </figure>

          <button
            v-if="extraFeedbackCount > 0 && !showAllFeedbacks"
            type="button"
            class="text-xs text-gray-400 hover:text-gray-300 underline underline-offset-2"
            @click="showAllFeedbacks = true"
          >
            {{ t('experience_card.feedback.see_others', { count: extraFeedbackCount }) }}
          </button>
        </div>

        <!-- Freelance → Company Feedback Section (owner only, proven experiences only) -->
        <div v-if="isOwner && !hideFeedbacks && experience.feedbacks && experience.experience_type === 'proven'" class="mt-4 border-t border-gray-700 pt-4">
          <!-- CTA: Rate company -->
          <div v-if="hasOpenFreelanceFeedback && !showFeedbackForm">
            <button @click="showFeedbackForm = true" class="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1">
              <Icon name="solar:star-bold-duotone" size="1rem" />
              {{ $t('company_feedback.rate_company_cta') }}
            </button>
          </div>

          <!-- Feedback form -->
          <div v-if="showFeedbackForm" class="mt-3 space-y-3">
            <p class="text-sm font-medium text-white">{{ $t('company_feedback.rate_company_title') }}</p>
            <div class="flex gap-2">
              <button v-for="s in [-1, 0, 1]" :key="s" @click="feedbackScore = s"
                class="px-3 py-1.5 rounded-md text-sm border transition-colors"
                :class="feedbackScore === s
                  ? (s === -1 ? 'bg-red-500/20 border-red-500 text-red-400' : s === 0 ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400' : 'bg-green-500/20 border-green-500 text-green-400')
                  : 'border-gray-600 text-gray-400 hover:border-gray-500'">
                {{ s === -1 ? $t('company_feedback.score_negative') : s === 0 ? $t('company_feedback.score_neutral') : $t('company_feedback.score_positive') }}
              </button>
            </div>
            <textarea v-model="feedbackComment" :placeholder="$t('company_feedback.comment_placeholder')" rows="3" maxlength="2000"
              class="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none" />
            <button @click="submitCompanyFeedback" :disabled="feedbackScore === null || feedbackLoading"
              class="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg">
              {{ feedbackLoading ? '...' : $t('company_feedback.submit_button') }}
            </button>
            <p v-if="feedbackError" class="text-xs text-red-400 mt-1">{{ feedbackError }}</p>
          </div>

          <!-- Submitted feedback display -->
          <div v-if="hasSubmittedFreelanceFeedback" class="mt-2 text-sm text-gray-400">
            <span class="font-medium text-white">{{ $t('company_feedback.already_rated') }}</span>
            <span v-if="freelanceFeedback.score === 1" class="ml-2">{{ $t('company_feedback.score_positive') }}</span>
            <span v-else-if="freelanceFeedback.score === -1" class="ml-2">{{ $t('company_feedback.score_negative') }}</span>
            <span v-else class="ml-2">{{ $t('company_feedback.score_neutral') }}</span>
            <p v-if="freelanceFeedback.comment" class="mt-1 text-gray-400 italic">{{ freelanceFeedback.comment }}</p>
          </div>

          <!-- Reply to negative company feedback -->
          <div v-for="fb in negativeUnrepliedFeedbacks" :key="fb.mission_feedback_id" class="mt-3">
            <div class="text-sm text-red-400">
              {{ fb.feedback_type === 'client' ? $t('company_feedback.review_from_client') : $t('company_feedback.review_from_manager') }}: {{ fb.comment || '' }}
            </div>
            <button v-if="replyingTo !== fb.mission_feedback_id" @click="replyingTo = fb.mission_feedback_id"
              class="mt-1 text-xs text-purple-400 hover:text-purple-300">
              {{ $t('company_feedback.reply_button') }}
            </button>
            <div v-if="replyingTo === fb.mission_feedback_id" class="mt-2 space-y-2">
              <textarea v-model="replyText" :placeholder="$t('company_feedback.reply_placeholder')" rows="2" maxlength="2000"
                class="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 resize-none" />
              <button @click="submitReply(fb.mission_feedback_id)" :disabled="!replyText.trim() || replyLoading"
                class="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-md">
                {{ replyLoading ? '...' : $t('company_feedback.reply_submit') }}
              </button>
              <p v-if="replyError" class="text-xs text-red-400 mt-1">{{ replyError }}</p>
            </div>
          </div>

          <!-- Show existing replies -->
          <template v-for="fb in receivedCompanyFeedbacks" :key="'reply-' + fb.mission_feedback_id">
            <div v-if="fb.reply_text" class="mt-2 ml-4 pl-3 border-l-2 border-gray-700">
              <p class="text-xs text-gray-500">{{ $t('company_feedback.reply_button') }}:</p>
              <p class="text-sm text-gray-300">{{ fb.reply_text }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>

    <b-modal v-model="showScopeModal" size="2xl" no-footer :title="t('experience_card.mission_scope')">
      <div class="flex flex-col gap-3 px-6 pb-6 text-sm text-gray-300">
        <div v-for="(block, i) in scopeBlocks" :key="i">
          <p v-if="block.header" class="mb-0.5 font-semibold text-gray-100">{{ block.header }}</p>
          <p v-if="block.body" class="whitespace-pre-line">{{ block.body }}</p>
        </div>
      </div>
    </b-modal>

    <b-split-experience-modal v-if="canSplit" v-model="splitModalOpen" :source="splitSource" @confirm="onSplitConfirm" />
  </div>
</template>

<script lang="ts" setup>
import BButton from '../forms/BButton.vue';
import BModal from '~/components/modal/BModal.vue';
import BSplitExperienceModal from '~/components/modal/experience/BSplitExperienceModal.vue';
import { authStore } from "~/stores/auth";
import { useStripeRedirect } from "~/composables/useStripeRedirect";
import { isSplittable, type SplitPeriod } from '~/help/experienceSplit';

const { t } = useI18n();

const showScopeModal = ref(false);

// Split the concatenated STAR description into blocks so the "see more" modal
// can render the STAR headers in bold.
const { toBlocks } = useStarDescription();
const scopeBlocks = computed(() => toBlocks(props.experience?.description));

interface Props {
  experience: any
  hideFeedbacks?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hideFeedbacks: false
})
const router = useRouter();
const route = useRoute()

const userStore = useUserStore()
const store = authStore()

const { user } = storeToRefs(userStore)
const { user: me } = storeToRefs(store);

const { isPreview } = useProfilePreviewMode();
const isOwner = computed(() => !isPreview.value && me.value && user.value && me.value.id === user.value.id);

// "Proven" experiences really happened on the platform (accepted invitation) and
// deserve the visual "BIG Approved" valorization vs. self-declared experiences.
const isProven = computed(() => props.experience?.experience_type === 'proven');

// --- Split a declarative CDI experience into several sub-periods (ticket #136) ---
// Only the owner, and only for declarative (never "proven"/BIG Approved) permanent
// experiences of >= 24 months, can split. Same isOwner guard as edit/delete above.
const splitModalOpen = ref(false);

const canSplit = computed(() =>
  isOwner.value &&
  isSplittable(
    {
      contract_type: props.experience?.contract_type,
      start: props.experience?.start_at,
      end: props.experience?.end_at,
      is_proven: props.experience?.experience_type === 'proven',
    },
    { requireDeclarative: true },
  )
);

const splitSource = computed(() => ({
  title: props.experience?.title || props.experience?.name || '',
  company: props.experience?.company?.public_name,
  start: (props.experience?.start_at || '').slice(0, 7), // YYYY-MM
  end: props.experience?.end_at ? props.experience.end_at.slice(0, 7) : null,
}));

const onSplitConfirm = async (segments: SplitPeriod[]) => {
  const { $api } = useNuxtApp();
  await $api().v2.experiences.split(
    props.experience.id,
    segments.map((s) => ({ name: s.title, start_at: s.start, end_at: s.end, skill_ids: s.skill_ids })),
  );
  // Same refresh mechanism used by submitCompanyFeedback/submitReply below: re-fetch
  // the profile's experiences list rather than emitting an event (this component has
  // no emits — edit/delete go through a route-query-driven modal instead).
  if (user.value) {
    experienceStore.fetchExperiences({ user_id: user.value.id, includes: ['feedbacks'] });
  }
};

const customerFeedback = computed(() => {
  return props.experience.feedbacks && props.experience.feedbacks.find(feedback => feedback.feedback_type === 'client');
});

const managerFeedback = computed(() => {
  return props.experience.feedbacks && props.experience.feedbacks.find(feedback => feedback.feedback_type === 'manager');
});

const freelanceFeedback = computed(() => {
  return props.experience.feedbacks?.find((feedback: any) => feedback.feedback_type === 'freelance');
});
const hasOpenFreelanceFeedback = computed(() => {
  return freelanceFeedback.value && freelanceFeedback.value.status === 'open';
});
const hasSubmittedFreelanceFeedback = computed(() => {
  return freelanceFeedback.value && freelanceFeedback.value.status === 'accepted';
});
const receivedCompanyFeedbacks = computed(() => {
  return (props.experience.feedbacks || []).filter((feedback: any) => feedback.feedback_type !== 'freelance');
});
const negativeUnrepliedFeedbacks = computed(() => {
  return receivedCompanyFeedbacks.value.filter((f: any) => f.score < 0 && f.reply_text === null);
});

// --- Public positive feedbacks (verbatims) — ticket #174 -------------------
// Shown to EVERYONE (visitor + owner) on the experience card, not just the owner.
// Only positive, accepted client/manager verbatims are public; the freelance's own
// rating of the company (feedback_type === 'freelance') is never shown publicly.
const companyName = computed(() => props.experience?.company?.public_name ?? '');

const publicPositiveFeedbacks = computed(() => {
  if (props.hideFeedbacks) return [];
  return (props.experience?.feedbacks ?? []).filter((f: any) =>
    f.status === 'accepted'
    && f.score > 0
    && f.feedback_type !== 'freelance'
    && typeof f.comment === 'string'
    && f.comment.trim() !== ''
  );
});

// Colour accent by author role: client = violet (primary), manager = blue
// (illustration.blue.light = #7B91FF). Same colours for proven AND declarative —
// the proven/declarative trust signal is carried by the "BIG Approved" banner.
const feedbackAccent = (f: any) => {
  if (f.feedback_type === 'manager') {
    return {
      border: 'border-illustration-blue-light',
      gradient: 'from-illustration-blue-light/[0.08]',
      chipBg: 'bg-illustration-blue-light/10',
      chipText: 'text-illustration-blue-light',
      label: t('experience_card.feedback.role_manager'),
    };
  }
  return {
    border: 'border-primary',
    gradient: 'from-primary/[0.08]',
    chipBg: 'bg-primary/10',
    chipText: 'text-primary',
    label: t('experience_card.feedback.role_client'),
  };
};

// Show at most 2 inline; the rest expand inline via a "+ N autres retours" link.
const showAllFeedbacks = ref(false);
const displayedFeedbacks = computed(() => {
  const list = showAllFeedbacks.value ? publicPositiveFeedbacks.value : publicPositiveFeedbacks.value.slice(0, 2);
  return list.map((f: any) => ({ ...f, accent: feedbackAccent(f) }));
});
const extraFeedbackCount = computed(() => Math.max(0, publicPositiveFeedbacks.value.length - 2));

// Per-verbatim "see more / see less" (long comments are clamped to 3 lines).
const expandedFeedbacks = reactive<Record<string, boolean>>({});
const toggleFeedback = (id: string) => { expandedFeedbacks[id] = !expandedFeedbacks[id]; };
const isLongFeedback = (f: any) => (f.comment?.length ?? 0) > 180;

const isManagerType = computed(() => {
  if (!props.experience || !props.experience.contract_type) return false;
  return ['permanent', 'fixed_term', 'internship', 'work_study'].includes(props.experience.contract_type);
});

/**
 * Check if the experience is ongoing (no end date)
 */
const isOngoing = computed(() => {
  return !props.experience?.end_at;
});

/**
 * Compute the experience weight based on contract type and validated feedback count.
 * - freelance/null: 0 feedback = 0.5, 1+ = 1.0
 * - CDI/CDD (permanent, fixed_term, internship, work_study): 0 = 0.5, 1 = 0.75, 2 = 1.0
 */
const experienceWeight = computed(() => {
  if (isOngoing.value) return 1.0;

  const validatedCount = props.experience?.validated_feedback_count ?? 0;

  if (isManagerType.value) {
    if (validatedCount >= 2) return 1.0;
    if (validatedCount === 1) return 0.75;
    return 0.5;
  }

  return validatedCount >= 1 ? 1.0 : 0.5;
});

const showWeightBadge = computed(() => {
  return !isOngoing.value && experienceWeight.value < 1.0;
});

const weightBadgeText = computed(() => {
  if (experienceWeight.value === 0.75) return t('experience_card.weight_75');
  return t('experience_card.weight_50');
});

const weightTooltipText = computed(() => {
  if (experienceWeight.value === 0.75) return t('experience_card.weight_75_tooltip');
  return t('experience_card.unvalidated_tooltip');
});

/**
 * Get the contract type label for display
 */
const contractTypeLabel = computed(() => {
  if (!props.experience?.contract_type) return '';
  const type = props.experience.contract_type;
  if (type === 'freelance') return t('experience_card.contract_type.freelance');
  if (type === 'permanent' || type === 'fixed_term') return t('experience_card.contract_type.permanent_fixed_term');
  if (type === 'internship') return t('experience_card.contract_type.internship');
  if (type === 'work_study') return t('experience_card.contract_type.work_study');
  return type;
});

/**
 * Generate the mission scope text from various fields
 */
const missionScopeText = computed(() => {
  const parts: string[] = [];

  // Add skills first if available
  if (props.experience?.skills?.length) {
    parts.push(...props.experience.skills.map((s: any) => s.name));
  }

  // Add industry
  if (props.experience?.industry?.name) {
    parts.push(props.experience.industry.name);
  }

  // Add market segments
  if (props.experience?.market_segments?.length) {
    parts.push(...props.experience.market_segments.map((s: any) => s.name));
  }

  // Add personas
  if (props.experience?.personas?.length) {
    parts.push(...props.experience.personas.map((p: any) => p.name));
  }

  // Add deal sizes
  if (props.experience?.deal_sizes?.length) {
    parts.push(...props.experience.deal_sizes.map((d: any) => d.name));
  }

  return parts.join(' - ') || '-';
});

/**
 * Calculate the completion percentage based on required fields only.
 * Scoring based on form required fields:
 * - Company info: 1 pt
 * - Title: 1 pt
 * - Start date: 1 pt
 * - Contract type: 1 pt
 * - Industry: 1 pt
 * - Company size: 1 pt
 * - Market segments: 1 pt
 * - Personas: 1 pt
 * - Deal sizes: 1 pt
 * - Skills: 1 pt
 * Total: 10 points = 100%
 *
 * Note: Optional fields (description, pipeline_revenue, end_at) are not counted.
 */
const completionPercentage = computed(() => {
  if (!props.experience) return 0;

  const exp = props.experience;
  let points = 0;
  const totalPoints = 10;

  // Required fields
  if (exp.company) points++;
  if (exp.title || exp.name) points++;
  if (exp.start_at) points++;
  if (exp.contract_type) points++;
  if (exp.industry) points++;
  if (exp.company_size) points++;

  // Required array fields
  if (exp.market_segments && exp.market_segments.length > 0) points++;
  if (exp.personas && exp.personas.length > 0) points++;
  if (exp.deal_sizes && exp.deal_sizes.length > 0) points++;
  if (exp.skills && exp.skills.length > 0) points++;

  return Math.round((points / totalPoints) * 100);
});

const onUpdateClick = () => {
  router.push({ ...route, query: { ...route.query, action: 'update', experience: props.experience.id } })
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  const months = [
    t('experience_card.months.jan'),
    t('experience_card.months.feb'),
    t('experience_card.months.mar'),
    t('experience_card.months.apr'),
    t('experience_card.months.may'),
    t('experience_card.months.jun'),
    t('experience_card.months.jul'),
    t('experience_card.months.aug'),
    t('experience_card.months.sep'),
    t('experience_card.months.oct'),
    t('experience_card.months.nov'),
    t('experience_card.months.dec')
  ];
  return `${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

// Dans votre composant
const formattedStartDate = computed(() => formatDate(props.experience?.start_at));
const formattedEndDate = computed(() => {
  // Si l'expérience n'a pas de date de fin, elle est en cours
  if (!props.experience?.end_at) {
    return t('experience_card.present');
  }
  return formatDate(props.experience.end_at);
});

const getDuration = computed(() => {
  const start = new Date(props.experience?.start_at);
  const end = props.experience?.end_at ? new Date(props.experience.end_at) : new Date();

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  if (end.getDate() < start.getDate()) {
    months--;
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  // +1: inclusive month counting (LinkedIn convention: Feb→May = 4 months, not 3)
  const totalMonths = years * 12 + months + 1;

  // Formatage du texte
  if (totalMonths < 12) {
    return `${totalMonths} ${t('experience_card.month')}`;
  } else if (totalMonths === 12) {
    return `1 ${t('experience_card.year')}`;
  } else {
    const y = Math.floor(totalMonths / 12);
    const m = totalMonths % 12;
    if (m === 0) {
      return `${y} ${t('experience_card.years')}`;
    } else {
      return `${y} ${t('experience_card.years')} ${m} ${t('experience_card.month')}`;
    }
  }
});

const formatNumberToK = (num: number) => {
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + 'k';
  }
  return num.toString();
};

const formattedPipelineRevenue = computed(() => {
  if (props.experience.pipeline_revenue) {
    return formatNumberToK(parseFloat(props.experience.pipeline_revenue));
  }
  return '0';
});

const onDeleteClick = () => {
  router.push({ ...route, query: { ...route.query, action: 'delete', resource: 'experience', resource_id: props.experience.id } })
}

const onCreateFeedback = () => {
  router.push({ ...route, query: { ...route.query, action: 'update', experience: props.experience.id, step: '3' } })
}

// Freelance → Company feedback state
const showFeedbackForm = ref(false);
const feedbackScore = ref<number | null>(null);
const feedbackComment = ref('');
const feedbackLoading = ref(false);
const replyingTo = ref<string | null>(null);
const replyText = ref('');
const replyLoading = ref(false);
const feedbackError = ref<string | null>(null);
const replyError = ref<string | null>(null);

const experienceStore = useExperienceStore();

const submitCompanyFeedback = async () => {
  if (feedbackScore.value === null) return;
  feedbackLoading.value = true;
  feedbackError.value = null;
  try {
    const { $api } = useNuxtApp();
    await $api().v2.mission_feedback.submitCompanyFeedback(props.experience.id || props.experience.mission_id, {
      score: feedbackScore.value,
      public_comment: feedbackComment.value || null,
    });
    showFeedbackForm.value = false;
    // Re-fetch experiences to reflect the new feedback
    if (user.value) {
      experienceStore.fetchExperiences({ user_id: user.value.id, includes: ['feedbacks'] });
    }
  } catch (error) {
    feedbackError.value = t('company_feedback.submit_error');
  } finally {
    feedbackLoading.value = false;
  }
};

const submitReply = async (feedbackId: string) => {
  if (!replyText.value.trim()) return;
  replyLoading.value = true;
  replyError.value = null;
  try {
    const { $api } = useNuxtApp();
    await $api().v2.mission_feedback.replyToFeedback(feedbackId, { reply_text: replyText.value });
    replyingTo.value = null;
    replyText.value = '';
    // Re-fetch experiences to reflect the reply
    if (user.value) {
      experienceStore.fetchExperiences({ user_id: user.value.id, includes: ['feedbacks'] });
    }
  } catch (error) {
    replyError.value = t('company_feedback.reply_error');
  } finally {
    replyLoading.value = false;
  }
};
</script>

<style scoped>
/* "BIG Approved" banner — top-right corner ribbon, fades out toward the left.
   Shown only for proven experiences (see isProven). Sits inside the card's
   rounded-2xl (16px) corner. pointer-events:none so it never blocks clicks. */
.big-approved-banner {
  position: absolute;
  top: 0;
  right: 0;
  height: 38px;
  width: 52%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 0 16px 0 56px;
  border-top-right-radius: 16px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(96, 40, 149, 0) 0%,
    rgba(96, 40, 149, 0.35) 30%,
    rgba(158, 100, 255, 0.85) 64%,
    #ff7be3 100%
  );
}

.big-approved-banner__mark {
  display: block;
  height: 19px;
  width: auto;
  flex: 0 0 auto;
  transform: translateY(-2px); /* optical vertical centering vs. the text */
  filter: drop-shadow(0 1px 2px rgba(70, 18, 110, 0.45));
}

.big-approved-banner__label {
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(80, 20, 120, 0.4);
}

.big-approved-banner__check {
  height: 15px;
  width: 15px;
  flex: 0 0 auto;
}
</style>